import {CheckItemsManager} from './check-items-manager';
import {Step} from '@app/modules/diagnostics/components/diagnostics/diagnostic-items/managers/step';

import {StepTestResult, TestPassState, TestQuestion, TestResponse, TestResponseCommands} from '@app/modules/diagnostics/components/diagnostics/diagnostic-items/models';
import {Observable, of} from 'rxjs';
import {WRTCProviderService} from '@app/modules/wrtc/services/providers/wrtc-provider';
import {delay} from 'rxjs/operators';
import {Role} from "@app/modules/shared/data/constants";

export class MicrophoneCheckManager  extends CheckItemsManager {
  icon = 'mic-g';
  name = 'Mic';

  constructor(userRole:Role, private wrtcProviderService: WRTCProviderService) {
    super(userRole);
    this.steps.push(new CheckingMicrophone(), new CheckingTapResponse());
  }

  testPostProcessing(){
    if (this.steps[0].testResult.status === TestPassState.FAILED) {
      this.steps[1].isHidden = true;
    }
  }
}

export class CheckingMicrophone extends Step {
  name = 'Checking microphone';
  title = 'Checking microphone...';
  mic;

  constructor() {
    super(true);
    this.testQuestion = new TestQuestion('Say something please. Is the bar moving as you speak?',
      [{text: 'Nope', value: 'no', color: 'blue', isBlock: false,isDisabled:false}, {
        text: 'It is moving',
        value: 'yes',
        color: 'blue',
        isBlock: false,
        isDisabled:false
      }],
    );
  }

  doRunStep() {
    return new Observable<TestResponse>(subscriber => {
      if (this.stepPayload === 'no') {
        this.testResult.status = TestPassState.FAILED;
        // eslint-disable-next-line max-len
        this.testResult.errorMassage = 'The teacher and other students won\'t be able to hear you. Try to restart your microphone, then click Retry. If you can\'t fix it now, click Skip. If you seek additional help, reach out to tech support using the chat bubble at the bottom or contact the office.';
        this.testResult.text='no microphone';
        this.testStatus='NO';
        subscriber.next(new TestResponse(TestResponseCommands.SHOW_ERROR));
      } else {
        this.testResult.status = TestPassState.PASSED;
        this.testResult.text = 'microphone listening';
        this.testStatus='OK';
        subscriber.next(new TestResponse(TestResponseCommands.NEXT));
      }
    });
  }

  override runStep(): Observable<TestResponse> {
    return new Observable<TestResponse>(subscriber => {
      navigator.mediaDevices.enumerateDevices().then(devices => {
        const audioInputDevices = devices.filter(device => device.kind === 'audioinput' && device.deviceId !== '');

        if (audioInputDevices.length === 0) {
          this.testResult.status = TestPassState.FAILED;
          // eslint-disable-next-line max-len
          this.testResult.errorMassage = 'No microphone found. The teacher and other students will not be able to hear you. For suggestions on fixing this, ask tech support, then retry this step. If you have done everything currently possible, skip this step.';
          this.testResult.text='no microphone';
          this.testStatus='NO';

          subscriber.next(new TestResponse(TestResponseCommands.SHOW_ERROR));
        } else {
          if (!this._isPreparedForTest) {
            subscriber.next(new TestResponse(TestResponseCommands.REQUEST_MIC, this.testQuestion));
          } else {
            this.doRunStep().pipe(delay(500)).subscribe((testResponse) => {
              subscriber.next(testResponse);
            });
          }
        }
      });
    });
  };
}

export class CheckingTapResponse extends Step {
name = 'Checking \'tap\' response';
title = 'Checking \'tap\' response...';

  constructor() {
    super(true);
    this.testQuestion = new TestQuestion('Tap lightly on headset microphone. Is the bar moving?',
      [{text: 'No', value: 'no', color: 'blue', isBlock: false,isDisabled:false}, {
        text: 'I\'m tapping and it\'s moving',
        value: 'yes',
        color: 'blue',
        isBlock: true,
        isDisabled:false
      }],
    );
  }
  override resetStep() {
    if (this.needToPrepareBeforeTest) {
      this.isPreparedForTest = false;
    }
    this.isHidden = false;
    this.testQuestion.answerOptions[1].isBlock = true;
    this.testResult = new StepTestResult();
  }

  doRunStep() {
    return new Observable<TestResponse>(subscriber => {
      if (this.stepPayload === 'no') {
        this.testResult.status = TestPassState.FAILED;
        this.testResult.errorMassage = 'Using the built-in or camera microphone creates noise pollution' +
          ' for the whole class by bringing in any noise that happens to be near you. For suggestions on how to fix this,' +
          ' consult the classroom guide or ask tech support, then retry this step. If you have done everything currently possible, skip this step.';
        this.testResult.text = 'using external mic';
        this.testStatus='NO';
        subscriber.next(new TestResponse(TestResponseCommands.SHOW_ERROR));
      } else {
        this.testResult.status = TestPassState.PASSED;
        this.testResult.text = 'using headset microphone';
        this.testStatus='OK';
        subscriber.next(new TestResponse(TestResponseCommands.NEXT));
      }
    });
  }
}
